import LinkedInIcon from "@mui/icons-material/LinkedIn";
import InstagramIcon from "@mui/icons-material/Instagram";
import FacebookIcon from "@mui/icons-material/Facebook";
import YouTubeIcon from "@mui/icons-material/YouTube";
import ArticleIcon from "@mui/icons-material/Article";
import MailOutlineIcon from "@mui/icons-material/MailOutline";

import { TikTokIcon } from "../../../components/icons/TikTokIcon";
import GoogleIcon from "@mui/icons-material/Google";
import { platformColors } from "../../../theme/theme";

/**
 * Single source for platform icon, name and colour in the content workflow; `inlineImages`: images inside the text;
 * `postLabel`: name of its only format when it isn't "Post".
 */
export const PLATFORM_META = {
  instagram: { label: "Instagram", Icon: InstagramIcon, color: platformColors.instagram },
  facebook: { label: "Facebook", Icon: FacebookIcon, color: platformColors.facebook },
  linkedin: { label: "LinkedIn", Icon: LinkedInIcon, color: platformColors.linkedin },
  tiktok: { label: "TikTok", Icon: TikTokIcon, color: platformColors.tiktok },
  google_business_profile: { label: "Perfil de empresa", Icon: GoogleIcon, color: platformColors.google_business_profile },
  blog: { label: "Blog", Icon: ArticleIcon, color: platformColors.blog, inlineImages: true, postLabel: "Artículo" },
  youtube: { label: "YouTube", Icon: YouTubeIcon, color: platformColors.youtube },
  email_marketing: { label: "Email", Icon: MailOutlineIcon, color: platformColors.email_marketing },
};

/** Every platform an adaptation can be generated for (email and YouTube have no adaptation flow). */
export const ADAPTATION_PLATFORMS = Object.keys(PLATFORM_META).filter(
  (platform) => !["email_marketing", "youtube"].includes(platform)
);

/** Networks bundle.social publishes (mirror of `integrations.bundle_social.PLATFORM_TYPES`). */
export const PUBLISHABLE_PLATFORMS = [
  "instagram",
  "facebook",
  "linkedin",
  "tiktok",
  "google_business_profile",
];

export const isPublishablePlatform = (platform) => PUBLISHABLE_PLATFORMS.includes(platform);

const formatUnknown = (platform) =>
  String(platform || "")
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

export const getPlatformMeta = (platform) =>
  PLATFORM_META[platform] || {
    label: formatUnknown(platform),
    Icon: ArticleIcon,
    color: platformColors.others,
  };

export const platformLabel = (platform) => getPlatformMeta(platform).label;

// Link and hashtag colours of each network, so previews read like the real post.
const LINK_COLOR = "#2563EB";
const HASHTAG_STYLES = {
  linkedin: { color: "#0A66C2", fontWeight: 600 },
  instagram: { color: "#00376B", fontWeight: 500 },
  facebook: { color: "#0064D1", fontWeight: 500 },
  tiktok: { color: "#161823", fontWeight: 700 },
  google_business_profile: { color: "#1A73E8", fontWeight: 500 },
  youtube: { color: "#065FD4", fontWeight: 500 },
};
const DEFAULT_HASHTAG_STYLE = { color: LINK_COLOR, fontWeight: 500 };

/** sx for rendered post text (see `markdownToHtmlSimple`): URLs in blue, hashtags per network. */
export const postTextSx = (platform) => ({
  "& a": { color: LINK_COLOR, textDecoration: "none", "&:hover": { textDecoration: "underline" } },
  "& .mk-hashtag": HASHTAG_STYLES[platform] || DEFAULT_HASHTAG_STYLE,
});

/** Rounded square with the network's icon, as in the cards of the mockups. */
export function PlatformIcon({ platform, size = 32, iconSize, filled = false, sx = {} }) {
  const { Icon, color } = getPlatformMeta(platform);
  const inner = iconSize || Math.round(size * 0.55);
  return (
    <span
      style={{
        width: size,
        height: size,
        minWidth: size,
        borderRadius: Math.round(size * 0.28),
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: filled ? color : `${color}14`,
        ...sx,
      }}
    >
      <Icon sx={{ fontSize: inner, color: filled ? "#fff" : color }} />
    </span>
  );
}

// ─── Formats and media rules (mirror of the backend `PLATFORM_FORMATS` / `FORMAT_RULES`) ───

export const VIDEO_MAX_MB = 150;
export const VIDEO_MIME_TYPES = ["video/mp4", "video/quicktime"];
const VIDEO_EXTENSIONS = [".mp4", ".mov"];
const VIDEO_ACCEPT = "video/mp4,video/quicktime,.mp4,.mov";
const MP4_ACCEPT = "video/mp4,.mp4";

const FORMAT_DEFS = {
  post: { label: "Post", noun: "Un post" },
  reel: { label: "Reel", noun: "Un Reel" },
  story: { label: "Historia", noun: "Una historia" },
  video: { label: "Vídeo", noun: "Un vídeo" },
};

/**
 * Rules per network and format. `images`/`videos`: media admitted; `mixed`: both in the same post;
 * durations in seconds, sizes in MB, bitrate in Mbps (as bundle.social: 1 Mbps = 1024² bps), resolution in px; `minAspect`/`maxAspect`: width/height of videos, `minImageAspect`/`maxImageAspect` of images; `noText`: the network doesn't publish the text;
 * `mp4Only`: MOV not admitted; `ratio`: shape of the preview box.
 */
const RULES = {
  instagram: {
    post: { images: true, videos: true, mixed: true, maxFiles: 10, minImageAspect: 4 / 5, maxImageAspect: 1.91, hint: "Hasta 10 fotos o vídeos, mezclados" },
    reel: { videos: true, maxFiles: 1, minDuration: 3, maxDuration: 15 * 60, maxBitrateMbps: 25, ratio: 9 / 16, hint: "1 vídeo vertical de 3 s a 15 min" },
    story: { images: true, videos: true, maxFiles: 1, minDuration: 3, maxDuration: 60, maxSizeMb: 100, maxBitrateMbps: 25, maxAspect: 5 / 3, maxImageAspect: 5 / 3, noText: true, ratio: 9 / 16, hint: "1 foto o 1 vídeo de 3 a 60 s" },
  },
  facebook: {
    post: { images: true, videos: true, maxFiles: 10, maxVideos: 1, maxDuration: 20 * 60, maxImageAspect: 1.91, hint: "Hasta 10 fotos o 1 vídeo de hasta 20 min (sin mezclar)" },
    reel: { videos: true, maxFiles: 1, minDuration: 3, maxDuration: 20 * 60, minWidth: 540, minHeight: 960, vertical: true, ratio: 9 / 16, hint: "1 vídeo vertical de 3 s a 20 min, mínimo 540×960" },
    story: { images: true, videos: true, maxFiles: 1, maxDuration: 60, maxImageAspect: 5 / 3, noText: true, ratio: 9 / 16, hint: "1 foto o 1 vídeo de hasta 60 s" },
  },
  tiktok: {
    post: { images: true, label: "Imágenes", noun: "Un carrusel de fotos", hint: "Carrusel de fotos" },
    video: { videos: true, maxFiles: 1, maxDuration: 10 * 60, minWidth: 360, minHeight: 360, maxWidth: 4096, maxHeight: 4096, ratio: 9 / 16, hint: "1 vídeo de hasta 10 min" },
  },
  linkedin: {
    post: { images: true, videos: true, maxFiles: 10, maxVideos: 1, minDuration: 3, maxDuration: 30 * 60, maxWidth: 4096, maxHeight: 4096, minAspect: 1 / 2.4, maxAspect: 2.4, mp4Only: true, hint: "Hasta 10 fotos, 1 vídeo MP4 de 3 s a 30 min o 1 PDF (sin mezclar)" },
  },
};

const DEFAULT_RULE = { images: true };

/** Formats a network offers ([{ value, label, hint }]); fewer than two → no selector. */
export const platformFormats = (platform) =>
  Object.entries(RULES[platform] || {}).map(([value, rule]) => ({
    value,
    label: rule.label || FORMAT_DEFS[value]?.label || value,
    hint: rule.hint || "",
  }));

/** Format of an adaptation, falling back to `post` (old data or a format the network doesn't have). */
export const adaptationFormat = (adaptation) => {
  const format = adaptation?.format || "post";
  return RULES[adaptation?.platform]?.[format] ? format : "post";
};

export const getFormatRule = (platform, format = "post") =>
  RULES[platform]?.[format] || RULES[platform]?.post || DEFAULT_RULE;

export const formatLabel = (platform, format = "post") => {
  const rule = RULES[platform]?.[format];
  return rule?.label || (format === "post" && PLATFORM_META[platform]?.postLabel) || FORMAT_DEFS[format]?.label || "Post";
};

/** "Un Reel de Instagram", "Una historia de Facebook"… for warning messages. */
export const formatNoun = (platform, format) => {
  const rule = getFormatRule(platform, format);
  const noun = rule.noun || FORMAT_DEFS[format]?.noun || "Un post";
  return `${noun} de ${platformLabel(platform)}`;
};

/**
 * `accept` of the file input for a network and format. Videos too when another format of the
 * network takes them (TikTok "Imágenes"): `sortPickedFiles` offers to switch.
 */
export const mediaAccept = (platform, format) => {
  const rule = getFormatRule(platform, format);
  const videos = rule.videos || platformSupportsVideo(platform);
  return [rule.images && "image/*", videos && (rule.mp4Only ? MP4_ACCEPT : VIDEO_ACCEPT)].filter(Boolean).join(",") || "image/*";
};

/** "MP4 o MOV", "MP4": video types the format takes. */
export const videoTypesLabel = (platform, format) => (getFormatRule(platform, format).mp4Only ? "MP4" : "MP4 o MOV");

/** True when the network admits video in some format (Instagram, Facebook, TikTok, LinkedIn). */
export const platformSupportsVideo = (platform) =>
  Object.values(RULES[platform] || {}).some((rule) => rule.videos);

export const isVideoMedia = (item) => item?.media_type === "video";
export const isDocumentMedia = (item) => item?.media_type === "document";
/** Old items without `media_type` are images. */
export const isImageMedia = (item) => !isVideoMedia(item) && !isDocumentMedia(item);

// LinkedIn takes one PDF per post, alone (backend `DOCUMENT_PLATFORMS` / `DOCUMENT_MAX_MB`).
export const DOCUMENT_MAX_MB = 50;
export const platformSupportsDocuments = (platform) => platform === "linkedin";
export const isPdfFile = (file) =>
  String(file?.name || "").toLowerCase().endsWith(".pdf") && (!file.type || file.type === "application/pdf");

export const isVideoFile = (file) => {
  const name = String(file?.name || "").toLowerCase();
  return file?.type?.startsWith("video/") || VIDEO_EXTENSIONS.some((ext) => name.endsWith(ext));
};

const isAcceptedVideoFile = (file, rule) => {
  const name = file.name.toLowerCase();
  if (rule.mp4Only) return file.type === "video/mp4" || (!file.type && name.endsWith(".mp4"));
  return VIDEO_MIME_TYPES.includes(file.type) || VIDEO_EXTENSIONS.some((ext) => name.endsWith(ext));
};

const megabytes = (bytes) => Math.round((bytes / (1024 * 1024)) * 10) / 10;

/** "45 s", "2 min", "2 min 5 s". */
export const formatDuration = (seconds) => {
  const total = Math.round(Number(seconds) || 0);
  if (total < 60) return `${total} s`;
  const minutes = Math.floor(total / 60);
  const rest = total % 60;
  return rest ? `${minutes} min ${rest} s` : `${minutes} min`;
};

/** "0:45", "12:05": the badge on a video. */
export const formatClock = (seconds) => {
  const total = Math.round(Number(seconds) || 0);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
};

/**
 * Splits picked files into the images and videos the format admits next to `current` media,
 * the rejected ones with the reason ([{ name, reason }]) and the videos that only fit
 * another format of the network (`videosForFormat`).
 */
export const sortPickedFiles = (platform, format, fileList, current = []) => {
  const rule = getFormatRule(platform, format);
  const formatName = formatLabel(platform, format);
  const noun = formatNoun(platform, format);
  const images = [];
  const videos = [];
  const rejected = [];
  const currentVideos = current.filter(isVideoMedia).length;
  const currentImages = current.filter(isImageMedia).length;
  const hasDocument = current.some(isDocumentMedia);
  const reject = (file, reason) => rejected.push({ name: file.name || "Archivo", reason });
  let videosForFormat = null; // { format, label, files }: videos that need another format of the network

  Array.from(fileList || []).forEach((file) => {
    const taken = images.length + videos.length;
    if (hasDocument) {
      reject(file, `${platformLabel(platform)} lleva un documento o imágenes, no ambos: quita el PDF antes.`);
      return;
    }
    if (isPdfFile(file)) {
      reject(
        file,
        platformSupportsDocuments(platform)
          ? `${platformLabel(platform)} lleva un documento o imágenes, no ambos: quita las imágenes antes.`
          : `${platformLabel(platform)} no admite documentos.`
      );
      return;
    }
    if (file.type?.startsWith("image/")) {
      if (!rule.images) reject(file, `${noun} solo admite vídeo.`);
      else if (rule.maxFiles === 1 && taken) reject(file, `${noun} lleva un solo archivo.`);
      else if (!rule.mixed && rule.videos && (currentVideos || videos.length)) reject(file, `${noun} lleva fotos o un vídeo, no ambos.`);
      else images.push(file);
      return;
    }
    if (!isVideoFile(file)) {
      reject(file, "Este tipo de archivo no se puede publicar.");
      return;
    }
    if (!platformSupportsVideo(platform)) {
      reject(file, `${platformLabel(platform)} solo admite imágenes.`);
    } else if (!rule.videos) {
      // TikTok "Imágenes": the video fits once the format is "Vídeo" (the card offers to switch).
      const videoFormat = platformFormats(platform).find((item) => getFormatRule(platform, item.value).videos);
      if (videoFormat && isAcceptedVideoFile(file, getFormatRule(platform, videoFormat.value)) && file.size <= VIDEO_MAX_MB * 1024 * 1024) {
        if (!videosForFormat) videosForFormat = { format: videoFormat.value, label: videoFormat.label, files: [] };
        videosForFormat.files.push(file);
      } else {
        reject(file, `${formatName} no admite vídeo${videoFormat ? `: cambia el formato a «${videoFormat.label}» para subirlo` : ""}.`);
      }
    } else if (!isAcceptedVideoFile(file, rule)) {
      reject(file, `Solo se admiten vídeos ${videoTypesLabel(platform, format)}.`);
    } else if (file.size > VIDEO_MAX_MB * 1024 * 1024) {
      reject(file, `Pesa ${megabytes(file.size)} MB y el máximo es ${VIDEO_MAX_MB} MB.`);
    } else if (rule.maxFiles === 1 && taken) {
      reject(file, `${noun} lleva un solo archivo.`);
    } else if (!rule.mixed && rule.images && (currentImages || images.length)) {
      reject(file, `${noun} lleva fotos o un vídeo, no ambos: quita las fotos o cambia el formato.`);
    } else if (rule.maxVideos && currentVideos + videos.length >= rule.maxVideos) {
      reject(file, `${noun} admite un solo vídeo.`);
    } else {
      videos.push(file);
    }
  });
  return { images, videos, rejected, videosForFormat };
};

/**
 * What doesn't fit the chosen format (non-blocking: the backend blocks when scheduling).
 * `media`: items of `adaptation.images` (old items without `media_type` are images).
 */
export const mediaWarnings = (platform, format, media, { checkAspect = true } = {}) => {
  const rule = getFormatRule(platform, format);
  const name = formatNoun(platform, format);
  const items = media || [];
  const videos = items.filter(isVideoMedia);
  const images = items.filter(isImageMedia);
  const documents = items.filter(isDocumentMedia);
  const warnings = [];

  if (documents.length) {
    if (!platformSupportsDocuments(platform)) {
      warnings.push(`${platformLabel(platform)} no admite documentos: quita el PDF.`);
    } else if (documents.length > 1) {
      warnings.push(`${platformLabel(platform)} admite un solo documento por publicación.`);
    } else if (images.length || videos.length) {
      warnings.push(`${platformLabel(platform)} lleva un documento o imágenes, no ambos: quita las imágenes o el PDF.`);
    }
  }

  if (images.length && !rule.images) {
    warnings.push(`${name} solo admite vídeo: quita ${images.length === 1 ? "la imagen" : `las ${images.length} imágenes`}.`);
  }
  if (videos.length && !rule.videos) {
    warnings.push(
      platformSupportsVideo(platform)
        ? `${name} no admite vídeo: cambia el formato o quita el vídeo.`
        : `${platformLabel(platform)} no publica vídeos: quita el vídeo.`
    );
  }
  if (images.length && videos.length && rule.images && rule.videos && !rule.mixed) {
    warnings.push(`${name} lleva fotos o un vídeo, no ambos.`);
  }
  if (rule.maxFiles && items.length > rule.maxFiles) {
    warnings.push(
      rule.maxFiles === 1
        ? `${name} lleva un solo archivo; ahora hay ${items.length}.`
        : `${name} admite como máximo ${rule.maxFiles} archivos; ahora hay ${items.length}.`
    );
  } else if (rule.maxVideos && videos.length > rule.maxVideos) {
    warnings.push(`${name} admite un solo vídeo; ahora hay ${videos.length}.`);
  }
  if (rule.videos && !rule.images && !videos.length) {
    warnings.push(`Sube un vídeo para publicarlo como ${formatLabel(platform, format)}.`);
  }

  if (rule.videos) {
    const several = videos.length > 1;
    videos.forEach((video, index) => {
      const which = several ? `el vídeo ${index + 1}` : "este vídeo";
      const { duration, width, height } = video;
      if (duration != null && rule.minDuration && duration < rule.minDuration) {
        warnings.push(`${name} dura como mínimo ${formatDuration(rule.minDuration)}; ${which} dura ${formatDuration(duration)}.`);
      }
      if (duration != null && rule.maxDuration && duration > rule.maxDuration) {
        warnings.push(`${name} dura como máximo ${formatDuration(rule.maxDuration)}; ${which} dura ${formatDuration(duration)}.`);
      }
      if (width && height) {
        if (rule.vertical && width >= height) {
          warnings.push(`${name} tiene que ser vertical; ${which} mide ${width}×${height}.`);
        }
        if (rule.maxAspect && width / height > rule.maxAspect) {
          warnings.push(`${name} no admite vídeos tan horizontales; ${which} mide ${width}×${height}.`);
        }
        if (rule.minAspect && width / height < rule.minAspect) {
          warnings.push(`${name} no admite vídeos tan verticales; ${which} mide ${width}×${height}.`);
        }
        if ((rule.minWidth && width < rule.minWidth) || (rule.minHeight && height < rule.minHeight)) {
          warnings.push(`${name} necesita al menos ${rule.minWidth}×${rule.minHeight}; ${which} mide ${width}×${height}.`);
        }
        if ((rule.maxWidth && width > rule.maxWidth) || (rule.maxHeight && height > rule.maxHeight)) {
          warnings.push(`${name} admite como máximo ${rule.maxWidth}×${rule.maxHeight}; ${which} mide ${width}×${height}.`);
        }
      }
      if (rule.maxSizeMb && video.file_size && video.file_size > rule.maxSizeMb * 1024 * 1024) {
        warnings.push(`${name} pesa como máximo ${rule.maxSizeMb} MB; ${which} pesa ${megabytes(video.file_size)} MB.`);
      }
      // Estimated from size and duration (audio included): enough to catch an export with too much quality.
      if (rule.maxBitrateMbps && video.file_size && duration) {
        const mbps = (video.file_size * 8) / duration / (1024 * 1024);
        if (mbps > rule.maxBitrateMbps) {
          warnings.push(
            `${name} admite como máximo ${rule.maxBitrateMbps} Mbps de bitrate; ${which} tiene unos ${Math.round(mbps)} Mbps. Comprímelo o expórtalo con menos calidad.`
          );
        }
      }
    });
  }

  if (checkAspect && rule.images) {
    images.forEach((image, index) => {
      const problem = imageAspectProblem(platform, format, image);
      if (!problem) return;
      const which = images.length > 1 ? `la imagen ${index + 1}` : "la imagen";
      warnings.push(`${name} ${problem}; ${which} mide ${image.width}×${image.height}. Recórtala.`);
    });
  }
  return warnings;
};

const aspectText = (aspect) => String(Math.round(aspect * 100) / 100).replace(".", ",");

/** Why the image's shape doesn't fit the format ("no admite imágenes tan …"), or "" (also when its size is unknown). */
export const imageAspectProblem = (platform, format, image) => {
  const { minImageAspect: min, maxImageAspect: max } = getFormatRule(platform, format);
  if (!isImageMedia(image) || !image.width || !image.height) return "";
  const aspect = image.width / image.height;
  if (max && aspect > max) return `no admite imágenes tan horizontales (máximo ${aspectText(max)}:1)`;
  if (min && aspect < min) return `no admite imágenes tan verticales (mínimo ${aspectText(min)}:1)`;
  return "";
};

/** Files that `format` can't take one by one (wrong type, duration, video shape, size); images are cropped, not removed. */
export const incompatibleMedia = (platform, format, media) =>
  (media || []).filter((item) => mediaWarnings(platform, format, [item], { checkAspect: false }).length > 0);

const EXPIRED_VIDEO_PATTERN = /caducad/i;

/** Error of a video that bundle.social no longer has ("El vídeo ha caducado en bundle.social…"). */
export const isVideoExpiredError = (text) => EXPIRED_VIDEO_PATTERN.test(String(text || ""));

/**
 * Publishing error tied to one media item: `{ message, mediaId }` from a `{ error, media_id }`
 * response, or null when the error isn't about a file.
 */
export const mediaErrorFrom = (err) => {
  const data = err?.response?.data;
  const message = typeof data?.error === "string" ? data.error : "";
  if (!message) return null;
  const mediaId = data?.media_id ?? null;
  if (mediaId == null && !isVideoExpiredError(message)) return null;
  return { message, mediaId };
};

/** Short description of an adaptation: format, media and length. */
export const adaptationMeta = (adaptation) => {
  if (!adaptation) return "";
  const parts = [];
  if (platformFormats(adaptation.platform).length > 1) {
    parts.push(formatLabel(adaptation.platform, adaptationFormat(adaptation)));
  }
  const media = adaptation.images || [];
  const videos = media.filter(isVideoMedia).length;
  const images = media.filter(isImageMedia).length;
  if (media.some(isDocumentMedia)) parts.push("Con documento PDF");
  if (videos > 1) parts.push(`${videos} vídeos`);
  else if (videos === 1) parts.push("Con vídeo");
  if (images > 1) parts.push(`${images} imágenes`);
  else if (images === 1) parts.push(videos ? "1 imagen" : "Con imagen");
  if (!media.length) parts.push("Solo texto");
  const text = adaptation.text || "";
  if (adaptation.platform === "blog") {
    const words = text.trim() ? text.trim().split(/\s+/).length : 0;
    parts.push(`${words.toLocaleString("es-ES")} palabras`);
    if (words) parts.push(`${Math.max(1, Math.round(words / 200))} min de lectura`);
  } else {
    parts.push(`${text.length.toLocaleString("es-ES")} caracteres`);
  }
  return parts.join(" · ");
};
